// AISCEvidenceDashboard.jsx: CR-2026-WEB-AISC-EVIDENCE (2026-09-29).
//
// Measured evidence, not a self-assessment. Every number comes from
// GET /api/aisc/evidence, which counts this company's actual records and the
// items that need attention. Nothing here is typed in by hand, and it is not
// an audit finding or a readiness rating.

import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link as RouterLink } from "react-router-dom";
import { Container, Card, Stack, Typography, Grid, Box, Button, Alert, LinearProgress, Link } from "@mui/material";
import PageGuide from "../../components/PageGuide";
import ApiCall from "../../utils/apicall";
import { StatusPill, KpiCard } from "../../components/WeldAssure";

const LINKS = {
  material: "/heat-management",
  procedures: "/wps-documents",
  welders: "/welder-wqtr",
  ncr: "/qa-ncr",
  bolting: "/bolting-demonstrations",
  documents: "/documents",
  projects: "/jobs",
};

const STATE = {
  ok: { label: "No open items", kind: "success" },
  attention: { label: "Needs attention", kind: "warning" },
  no_records: { label: "No records yet", kind: "neutral" },
};

function day(v) {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
}

export default function AISCEvidenceDashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true); setError("");
    try {
      const res = await ApiCall.get("/aisc/evidence");
      setData(res.data?.data || null);
    } catch (err) {
      setError(err?.response?.data?.message || "Could not load the evidence summary.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const s = data?.summary;
  return (
    <>
      <Helmet><title>AISC Evidence | Shop Floor Mgmt</title></Helmet>
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h4">AISC Evidence Dashboard</Typography>
            <Typography variant="body2" color="text.secondary">
              Counts of your records by AISC area, and what needs attention. Measured from your data{data?.generatedAt ? ` at ${new Date(data.generatedAt).toLocaleString()}` : ""}.
            </Typography>
          </Box>
          <Button variant="outlined" onClick={load} disabled={loading}>Refresh</Button>
        </Stack>

        <PageGuide
          why="See at a glance where your quality records stand, from the records themselves."
          who="The QM and managers preparing for surveillance or an audit."
          what="Record counts per area, with expired, overdue, open, unverified and failed items called out."
          how="Open an area's register to work the items. Numbers update when you refresh."
          when="Weekly, and before any audit or customer visit."
          standard="AISC 207 certification programme areas"
        />

        {loading && <LinearProgress sx={{ my: 2 }} />}
        {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}

        {s && (
          <Grid container spacing={2} sx={{ my: 1 }}>
            <Grid item xs={6} md={3}><KpiCard label="AREAS MEASURED" value={s.areas} /></Grid>
            <Grid item xs={6} md={3}><KpiCard label="AREAS WITH RECORDS" value={s.withRecords} /></Grid>
            <Grid item xs={6} md={3}><KpiCard label="NEED ATTENTION" value={s.needingAttention} sub={s.needingAttention ? "open items" : "none"} subKind={s.needingAttention ? "warning" : "success"} /></Grid>
            <Grid item xs={6} md={3}><KpiCard label="NO RECORDS YET" value={s.noRecords} /></Grid>
          </Grid>
        )}

        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {(data?.areas || []).map((a) => {
            const st = STATE[a.state] || STATE.no_records;
            const pct = a.total ? Math.round((a.ok.count / a.total) * 100) : 0;
            return (
              <Grid item xs={12} md={6} key={a.key}>
                <Card variant="outlined" sx={{ p: 2, height: "100%" }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700}>{a.title}</Typography>
                      <Typography variant="caption" color="text.secondary">Source: {a.source}{day(a.lastRecordAt) ? ` · last change ${day(a.lastRecordAt)}` : ""}</Typography>
                    </Box>
                    <StatusPill label={st.label} kind={st.kind} />
                  </Stack>
                  <Typography variant="body2" sx={{ mt: 1.5 }}>
                    <strong>{a.total}</strong> record{a.total === 1 ? "" : "s"} · {a.ok.label}: <strong>{a.ok.count}</strong>
                  </Typography>
                  {a.total > 0 && <LinearProgress variant="determinate" value={pct} color={a.state === "ok" ? "success" : "warning"} sx={{ my: 1, height: 6, borderRadius: 3 }} />}
                  {a.attention.map((x) => (
                    <Typography key={x.label} variant="body2" sx={{ color: "#B26A00" }}>{x.label}: <strong>{x.count}</strong></Typography>
                  ))}
                  {LINKS[a.key] && (
                    <Link component={RouterLink} to={LINKS[a.key]} variant="body2" sx={{ display: "inline-block", mt: 1 }}>Open register</Link>
                  )}
                </Card>
              </Grid>
            );
          })}
        </Grid>

        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 3, mb: 4 }}>
          This page reports what your records show. It is not an audit finding, a certification readiness rating, or advice on how to meet AISC requirements.
        </Typography>
      </Container>
    </>
  );
}
